import { Rng } from '../../core/rng';
import { stationDef, stationSeed, type StationId } from './stations';

/**
 * Pure generation of everything on the radio that is not a song: DJ links (formant "speech": no
 * words, but phrases, stress, intonation and pauses), adverts with stings and a product sound, the
 * Kessmouth Evening Post news (pips, teleprinter under the reader), weather, travel, sport, phone-ins
 * (presenter, ring, caller down a telephone line), time checks and the station jingles.
 */

export type Speaker = 'dj' | 'reader' | 'announcer' | 'caller' | 'caller2';

export interface Syllable {
  kind: 'syl';
  /** Seconds from the item start. */
  t: number;
  dur: number;
  f0: number;
  f0End: number;
  f1: number;
  f1End: number;
  f2: number;
  f2End: number;
  f3: number;
  amp: number;
  /** 0..1 strength of a fricative hiss at the onset (s / f / sh). */
  fric: number;
  /** 0..1 strength of a plosive click at the onset (p / t / k). */
  plosive: number;
  speaker: Speaker;
}

export type CueInst = 'vibes' | 'brass' | 'bell' | 'rhodes' | 'hammond' | 'strings' | 'flute' | 'melodica' | 'piano' | 'guitar';

export interface NoteCue {
  kind: 'note';
  t: number;
  midi: number;
  dur: number;
  inst: CueInst;
  vel: number;
}

export type SfxName = 'ring' | 'pip' | 'pipLong' | 'teleprinter' | 'bell' | 'register' | 'horn' | 'chime' | 'static' | 'kickDrum' | 'applause';

export interface SfxCue {
  kind: 'sfx';
  t: number;
  name: SfxName;
  /** Seconds (teleprinter / applause / static). */
  dur: number;
  level: number;
}

export type Cue = Syllable | NoteCue | SfxCue;

export type TalkKind = 'link' | 'advert' | 'jingle' | 'news' | 'weather' | 'phoneIn' | 'timecheck' | 'traffic' | 'sport' | 'pips';

export interface TalkItem {
  kind: TalkKind;
  title: string;
  duration: number;
  cues: Cue[];
  /** Seconds of speech inside the item (for ducking the music under a DJ). */
  speechStart: number;
  speechEnd: number;
}

export const TALK_F0_MIN = 85;
export const TALK_F0_MAX = 260;

interface Voice {
  f0: number;
  /** Formant scale (vocal tract length): < 1 deeper, > 1 smaller. */
  formant: number;
  /** Syllables per second. */
  pace: number;
  breath: number;
  speaker: Speaker;
  /** 0..1 pitch range (excitement). */
  range: number;
}

/** Vowel formant targets (Hz) for an average male tract: [f1, f2, f3]. */
const VOWELS: readonly (readonly [number, number, number])[] = [
  [700, 1200, 2600], // a
  [530, 1850, 2500], // e
  [320, 2300, 3000], // i
  [500, 900, 2500], // o
  [350, 800, 2300], // u
  [500, 1500, 2500], // schwa
  [600, 1000, 2500], // aw
  [420, 2000, 2600], // ih
];

function clampF0(f: number): number {
  return Math.max(TALK_F0_MIN, Math.min(TALK_F0_MAX, f));
}

export type Intonation = 'statement' | 'question' | 'excited' | 'read' | 'list';

/**
 * Speech: `phrases` phrases of syllables with a falling (or rising) f0 contour, stress, varied vowels
 * and pauses between phrases. Returns the cues and the end time.
 */
export function speech(rng: Rng, v: Voice, t0: number, phrases: number, into: Intonation): { cues: Syllable[]; end: number } {
  const cues: Syllable[] = [];
  let t = t0;
  for (let p = 0; p < phrases; p++) {
    const n = into === 'read' ? rng.int(7, 12) : into === 'list' ? rng.int(3, 5) : rng.int(4, 9);
    const base = v.f0 * (into === 'excited' ? 1.12 : into === 'read' ? 0.98 : 1) * rng.range(0.95, 1.05);
    const stressAt = new Set<number>();
    for (let i = 0; i < n; i++) if (rng.chance(into === 'excited' ? 0.4 : 0.28) || i === 0) stressAt.add(i);
    for (let i = 0; i < n; i++) {
      const progress = i / Math.max(1, n - 1);
      const stressed = stressAt.has(i);
      const sylLen = (1 / v.pace) * rng.range(0.7, 1.15) * (stressed ? 1.25 : 1);
      const dur = Math.max(0.06, Math.min(0.32, sylLen * 0.8));
      // Contour: declination over the phrase, a rise at the end of a question, a lift on stress.
      let contour = 1 - 0.12 * progress * (into === 'read' ? 1.2 : 1);
      if (into === 'question' && progress > 0.7) contour += 0.18 * (progress - 0.7) / 0.3;
      if (into === 'list' && i === n - 1 && p < phrases - 1) contour += 0.08;
      const lift = stressed ? 1 + 0.08 * (1 + v.range) : 1;
      const wobble = rng.range(-0.03, 0.03) * (1 + v.range);
      const f0 = clampF0(base * contour * lift * (1 + wobble));
      const f0End = clampF0(f0 * (stressed ? rng.range(0.9, 0.98) : rng.range(0.94, 1.04)) * (into === 'question' && i === n - 1 ? 1.12 : 1));
      const vow = rng.pick(VOWELS);
      const vow2 = rng.chance(0.35) ? rng.pick(VOWELS) : vow;
      const fs = v.formant;
      cues.push({
        kind: 'syl',
        t,
        dur,
        f0,
        f0End,
        f1: vow[0] * fs,
        f1End: (vow2[0] * 0.5 + vow[0] * 0.5) * fs,
        f2: vow[1] * fs,
        f2End: (vow2[1] * 0.6 + vow[1] * 0.4) * fs,
        f3: vow[2] * fs,
        amp: (stressed ? rng.range(0.85, 1) : rng.range(0.55, 0.78)) * (i === n - 1 ? 0.85 : 1),
        fric: rng.chance(0.3) ? rng.range(0.4, 1) * (0.6 + v.breath) : 0,
        plosive: rng.chance(0.35) ? rng.range(0.4, 1) : 0,
        speaker: v.speaker,
      });
      t += sylLen + (stressed ? 0.015 : 0);
    }
    t += into === 'read' ? rng.range(0.3, 0.55) : into === 'excited' ? rng.range(0.12, 0.35) : rng.range(0.25, 0.7);
  }
  return { cues, end: t };
}

function djVoice(station: StationId, rng: Rng): Voice {
  const d = stationDef(station).dj;
  return { f0: d.f0 * rng.range(0.97, 1.03), formant: d.formant, pace: d.pace, breath: d.breath, speaker: 'dj', range: station === 'ska' || station === 'beat' ? 0.8 : 0.4 };
}

const READER: Voice = { f0: 112, formant: 0.96, pace: 4.6, breath: 0.2, speaker: 'reader', range: 0.2 };
const ANNOUNCER: Voice = { f0: 126, formant: 1.02, pace: 5.4, breath: 0.25, speaker: 'announcer', range: 0.7 };

function callerVoice(rng: Rng, second = false): Voice {
  const woman = rng.chance(0.5);
  return { f0: woman ? rng.range(175, 215) : rng.range(100, 135), formant: woman ? 1.15 : rng.range(0.92, 1.04), pace: rng.range(4.6, 6.2), breath: 0.35, speaker: second ? 'caller2' : 'caller', range: 0.6 };
}

// ---------------------------------------------------------------------------------------------
// Jingles: each station's sonic logo.

export function stationJingle(station: StationId, t0 = 0): { cues: Cue[]; end: number } {
  const J = (t: number, midi: number, dur: number, inst: CueInst, vel = 0.85): NoteCue => ({ kind: 'note', t: t0 + t, midi, dur, inst, vel });
  switch (station) {
    case 'soul':
      // Vibes climb to a Dm9 bloom with a Rhodes bass note.
      return { cues: [J(0, 62, 0.3, 'vibes'), J(0.16, 65, 0.3, 'vibes'), J(0.32, 69, 0.3, 'vibes'), J(0.48, 72, 1.1, 'vibes', 0.9), J(0.48, 76, 1.1, 'vibes', 0.7), J(0.48, 50, 1.1, 'rhodes', 0.6), J(0.48, 57, 1.1, 'rhodes', 0.5)], end: t0 + 1.7 };
    case 'beat':
      // Brass punches: "Hea-vy Pen-nant!" with a drum hit.
      return { cues: [J(0, 64, 0.12, 'brass'), J(0.14, 64, 0.12, 'brass'), J(0.3, 67, 0.12, 'brass'), J(0.44, 69, 0.12, 'brass'), J(0.62, 71, 0.45, 'brass', 1), J(0.62, 76, 0.45, 'brass', 0.8), { kind: 'sfx', t: t0 + 0.62, name: 'kickDrum', dur: 0, level: 0.8 }], end: t0 + 1.2 };
    case 'easy':
      // A string swell resolving onto a major 9th, a flute on top.
      return { cues: [J(0, 60, 1.4, 'strings', 0.6), J(0, 64, 1.4, 'strings', 0.55), J(0, 67, 1.4, 'strings', 0.5), J(0.5, 71, 0.9, 'strings', 0.5), J(0.7, 74, 0.8, 'flute', 0.8), J(1.0, 76, 1.0, 'flute', 0.9)], end: t0 + 2.2 };
    case 'ska':
      // Melodica calling across the water, a piano skank underneath.
      return { cues: [J(0, 67, 0.18, 'melodica'), J(0.24, 71, 0.18, 'melodica'), J(0.48, 74, 0.18, 'melodica'), J(0.72, 79, 0.6, 'melodica', 0.95), J(0.24, 55, 0.1, 'piano', 0.6), J(0.24, 59, 0.1, 'piano', 0.6), J(0.72, 55, 0.6, 'piano', 0.6), J(0.72, 62, 0.6, 'piano', 0.5)], end: t0 + 1.5 };
    default:
      // Borough 1071: four bright blips and a bell.
      return { cues: [J(0, 72, 0.11, 'piano'), J(0.13, 76, 0.11, 'piano'), J(0.26, 79, 0.11, 'piano'), J(0.39, 84, 0.4, 'piano', 0.9), J(0.39, 88, 0.6, 'bell', 0.5)], end: t0 + 1.1 };
  }
}

// ---------------------------------------------------------------------------------------------
// Items

const ADVERTISERS: readonly { name: string; sfx: SfxName; key: number }[] = [
  { name: 'Halloran Motors', sfx: 'horn', key: 0 },
  { name: "Pike's Respray", sfx: 'horn', key: 7 },
  { name: 'Kessmouth Savings Bank', sfx: 'register', key: 5 },
  { name: 'The Crooked Anchor', sfx: 'chime', key: 2 },
  { name: 'Marlow Street Market', sfx: 'register', key: 9 },
  { name: 'The Velvet Lamp', sfx: 'chime', key: 4 },
  { name: "Gorringe's Scrap Metals", sfx: 'bell', key: 10 },
  { name: 'Parade Arcade Furnishings', sfx: 'chime', key: 3 },
  { name: 'Kessmouth Evening Post', sfx: 'bell', key: 7 },
  { name: 'Tench & Sons Removals', sfx: 'horn', key: 0 },
];

const PHONE_TOPICS = ['Parking rows', 'Wardens wanted: your say', 'Bin day', 'The new flats', 'Noise from the docks', 'Pub hours', 'The Ring Road', 'Street lighting', 'The Parade market', 'Buses after dark'];

function sting(rng: Rng, key: number, t0: number, up: boolean): { cues: Cue[]; end: number } {
  const root = 60 + key;
  const notes = up ? [0, 4, 7, 12] : [12, 7, 4, 0];
  const inst: CueInst = rng.pick(['brass', 'vibes', 'piano', 'hammond']);
  const cues: Cue[] = notes.map((iv, i) => ({ kind: 'note', t: t0 + i * 0.11, midi: root + iv, dur: i === 3 ? 0.5 : 0.12, inst, vel: 0.8 }));
  cues.push({ kind: 'note', t: t0 + 0.33, midi: root - 12 + (up ? 0 : 0), dur: 0.5, inst: 'rhodes', vel: 0.55 });
  return { cues, end: t0 + 0.9 };
}

function speechBounds(cues: readonly Cue[]): { speechStart: number; speechEnd: number } {
  let s = Infinity;
  let e = 0;
  for (const c of cues) {
    if (c.kind !== 'syl') continue;
    s = Math.min(s, c.t);
    e = Math.max(e, c.t + c.dur);
  }
  return { speechStart: s === Infinity ? 0 : s, speechEnd: e };
}

/** A DJ link between records: a few phrases, excited or unhurried by station. */
export function linkItem(station: StationId, n: number, talkOver: boolean): TalkItem {
  const rng = new Rng(stationSeed(station, 21, n));
  const v = djVoice(station, rng);
  const into: Intonation = station === 'easy' ? 'statement' : station === 'soul' ? (rng.chance(0.3) ? 'question' : 'statement') : 'excited';
  const phrases = talkOver ? rng.int(2, 3) : rng.int(2, 4);
  const s = speech(rng, v, 0.15, phrases, into);
  const cues: Cue[] = [...s.cues];
  return { kind: 'link', title: 'In the studio', duration: s.end + 0.2, cues, ...speechBounds(cues) };
}

/** A time check: the station jingle's bell, then one short phrase. */
export function timecheckItem(station: StationId, n: number): TalkItem {
  const rng = new Rng(stationSeed(station, 22, n));
  const v = djVoice(station, rng);
  const cues: Cue[] = [{ kind: 'note', t: 0, midi: 88, dur: 0.6, inst: 'bell', vel: 0.5 }];
  const s = speech(rng, v, 0.5, 1, 'list');
  cues.push(...s.cues);
  return { kind: 'timecheck', title: 'Time check', duration: s.end + 0.3, cues, ...speechBounds(cues) };
}

/** An advert: sting up, the announcer, the product's sound, a closing line, sting down. */
export function advertItem(station: StationId, n: number): TalkItem {
  const rng = new Rng(stationSeed(station, 23, n));
  const ad = ADVERTISERS[rng.int(0, ADVERTISERS.length - 1)] as { name: string; sfx: SfxName; key: number };
  const cues: Cue[] = [];
  const open = sting(rng, ad.key, 0, true);
  cues.push(...open.cues);
  const voice = { ...ANNOUNCER, f0: ANNOUNCER.f0 * rng.range(0.9, 1.1) };
  const s1 = speech(rng, voice, open.end, rng.int(2, 3), 'excited');
  cues.push(...s1.cues);
  cues.push({ kind: 'sfx', t: s1.end + 0.05, name: ad.sfx, dur: 0, level: 0.8 });
  const s2 = speech(rng, voice, s1.end + 0.75, rng.int(1, 2), 'statement');
  cues.push(...s2.cues);
  const close = sting(rng, ad.key, s2.end + 0.1, false);
  cues.push(...close.cues);
  return { kind: 'advert', title: `Advert: ${ad.name}`, duration: close.end + 0.35, cues, ...speechBounds(cues) };
}

/** The Greenwich-style pips: five short, one long, on the hour. */
export function pipsItem(station: StationId, n: number): TalkItem {
  void station;
  void n;
  const cues: Cue[] = [];
  for (let i = 0; i < 5; i++) cues.push({ kind: 'sfx', t: i, name: 'pip', dur: 0.1, level: 0.7 });
  cues.push({ kind: 'sfx', t: 5, name: 'pipLong', dur: 0.5, level: 0.7 });
  return { kind: 'pips', title: 'The pips', duration: 6.2, cues, speechStart: 0, speechEnd: 0 };
}

/** The news, read from the Kessmouth Evening Post: a teleprinter clatters under the first story. */
export function newsItem(station: StationId, n: number): TalkItem {
  const rng = new Rng(stationSeed(station, 24, n));
  const cues: Cue[] = [];
  const j = stationJingle(station, 0);
  cues.push(...j.cues);
  cues.push({ kind: 'sfx', t: j.end - 0.2, name: 'teleprinter', dur: 4.5, level: 0.35 });
  const stories = rng.int(3, 4);
  let t = j.end + 0.3;
  for (let s = 0; s < stories; s++) {
    const sp = speech(rng, READER, t, rng.int(2, 3), 'read');
    cues.push(...sp.cues);
    t = sp.end + rng.range(0.4, 0.8);
  }
  const closing = speech(rng, READER, t, 1, 'list');
  cues.push(...closing.cues);
  return { kind: 'news', title: 'The Kessmouth Evening Post news', duration: closing.end + 0.5, cues, ...speechBounds(cues) };
}

export function weatherItem(station: StationId, n: number): TalkItem {
  const rng = new Rng(stationSeed(station, 25, n));
  const cues: Cue[] = [{ kind: 'note', t: 0, midi: 79, dur: 0.5, inst: 'vibes', vel: 0.6 }, { kind: 'note', t: 0.18, midi: 84, dur: 0.6, inst: 'vibes', vel: 0.6 }];
  const v = { ...READER, f0: READER.f0 * 1.06, pace: 5 };
  const s = speech(rng, v, 0.9, rng.int(2, 3), 'list');
  cues.push(...s.cues);
  return { kind: 'weather', title: 'Weather where you are', duration: s.end + 0.4, cues, ...speechBounds(cues) };
}

export function trafficItem(station: StationId, n: number): TalkItem {
  const rng = new Rng(stationSeed(station, 26, n));
  const cues: Cue[] = [{ kind: 'sfx', t: 0, name: 'horn', dur: 0, level: 0.5 }];
  const v = djVoice(station, rng);
  const s = speech(rng, v, 0.8, rng.int(2, 3), 'statement');
  cues.push(...s.cues);
  return { kind: 'traffic', title: 'Travel: the Ring Road and the Parade', duration: s.end + 0.3, cues, ...speechBounds(cues) };
}

export function sportItem(station: StationId, n: number): TalkItem {
  const rng = new Rng(stationSeed(station, 27, n));
  const cues: Cue[] = [{ kind: 'sfx', t: 0, name: 'applause', dur: 1.6, level: 0.4 }];
  const v = { ...ANNOUNCER, pace: 5.8, range: 0.5 };
  const s = speech(rng, v, 0.6, rng.int(3, 4), 'read');
  cues.push(...s.cues);
  return { kind: 'sport', title: 'Sports desk', duration: s.end + 0.3, cues, ...speechBounds(cues) };
}

/** Phone-in: presenter, the line rings, a caller (down the phone), the presenter again, maybe a second caller. */
export function phoneInItem(station: StationId, n: number): TalkItem {
  const rng = new Rng(stationSeed(station, 28, n));
  const topic = rng.pick(PHONE_TOPICS);
  const cues: Cue[] = [];
  const host = djVoice(station, rng);
  const s1 = speech(rng, host, 0.2, rng.int(1, 2), 'statement');
  cues.push(...s1.cues);
  let t = s1.end + 0.2;
  cues.push({ kind: 'sfx', t, name: 'ring', dur: 0.4, level: 0.5 });
  t += 1.2;
  const caller = callerVoice(rng);
  const s2 = speech(rng, caller, t, rng.int(2, 4), rng.chance(0.4) ? 'excited' : 'statement');
  cues.push(...s2.cues);
  const s3 = speech(rng, host, s2.end + 0.25, rng.int(1, 2), rng.chance(0.5) ? 'question' : 'statement');
  cues.push(...s3.cues);
  t = s3.end;
  if (rng.chance(0.6)) {
    const s4 = speech(rng, caller, t + 0.2, rng.int(1, 3), 'statement');
    cues.push(...s4.cues);
    t = s4.end;
    const s5 = speech(rng, host, t + 0.2, 1, 'statement');
    cues.push(...s5.cues);
    t = s5.end;
  }
  return { kind: 'phoneIn', title: `Phone-in: ${topic}`, duration: t + 0.4, cues, ...speechBounds(cues) };
}

export function jingleItem(station: StationId, n: number): TalkItem {
  void n;
  const j = stationJingle(station, 0.05);
  return { kind: 'jingle', title: 'Station ident', duration: j.end + 0.25, cues: j.cues, speechStart: 0, speechEnd: 0 };
}

/** Build a talk item of a kind. `n` is its running number of that kind on the station. */
export function talkItem(kind: TalkKind, station: StationId, n: number, talkOver = false): TalkItem {
  switch (kind) {
    case 'link':
      return linkItem(station, n, talkOver);
    case 'advert':
      return advertItem(station, n);
    case 'jingle':
      return jingleItem(station, n);
    case 'news':
      return newsItem(station, n);
    case 'weather':
      return weatherItem(station, n);
    case 'phoneIn':
      return phoneInItem(station, n);
    case 'timecheck':
      return timecheckItem(station, n);
    case 'traffic':
      return trafficItem(station, n);
    case 'sport':
      return sportItem(station, n);
    default:
      return pipsItem(station, n);
  }
}
